import { useParams, Money, Pct, Num, Kpi, Shell, ShareRow, money0 } from "./ui";
import { retention, expansionNeededForNrr, headroom, renewalUplift, multiYearFactor, discountLeakage, toolTax, fmtPct } from "./formulas";

const tone = (x, good, ok) => (x >= good ? "#bfe6d1" : x >= ok ? "#f4d992" : "#f5c3c3");

export function NrrGrr() {
  const [v, set] = useParams({ start: 5_000_000, churn: 300_000, contraction: 150_000, expansion: 900_000, target: 115 });
  const r = retention({ startArr: v.start, churn: v.churn, contraction: v.contraction, expansion: v.expansion });
  const need = expansionNeededForNrr({ startArr: v.start, churn: v.churn, contraction: v.contraction, targetNrr: v.target / 100 });
  return (
    <Shell
      form={<>
        <div className="eyebrow">Your last 12 months</div>
        <Money id="start" label="ARR at the start of the period" value={v.start} onChange={set("start")} step={50000} />
        <div className="line" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <Money id="churn" label="ARR lost to churn" value={v.churn} onChange={set("churn")} step={10000} hint="Logos that left" />
          <Money id="contraction" label="ARR lost to contraction" value={v.contraction} onChange={set("contraction")} step={10000} hint="Downgrades, seat reductions" />
        </div>
        <Money id="expansion" label="Expansion ARR from existing customers" value={v.expansion} onChange={set("expansion")} step={10000} hint="Upsell, cross-sell, price increases" />
        <Pct id="target" label="Target NRR" value={v.target} onChange={set("target")} min={90} max={150} />
      </>}
      out={<>
        <div className="eyebrow" style={{ color: "#d9b45b" }}>Retention</div>
        <div className="calc__kpis">
          <Kpi k="Gross revenue retention" v={fmtPct(r.grr)} s="what you kept, before expansion" testid="grr" tone={tone(r.grr, 0.9, 0.8)} />
          <Kpi k="Net revenue retention" v={fmtPct(r.nrr)} s="including expansion" testid="nrr" tone={tone(r.nrr, 1.1, 1.0)} />
          <Kpi k="End-of-period ARR" v={money0(r.endArr)} s={`from ${money0(v.start)}`} />
        </div>
        <div className="gov">
          <div className="gov__title">To reach {v.target}% NRR</div>
          <div className="gov__why" data-testid="nrr-need">You need <b style={{ color: "#fff" }}>{money0(need)}</b> of expansion against this churn and contraction — {need > v.expansion ? <>that is <b style={{ color: "#fff" }}>{money0(need - v.expansion)}</b> more than you booked.</> : <>you are already there by <b style={{ color: "#fff" }}>{money0(v.expansion - need)}</b>.</>} Expansion is the only lever in this equation that does not require a new logo.</div>
        </div>
        <ShareRow name="nrr" />
      </>}
      note="Benchmarks (public SaaS, 2024–25): median NRR ~105–110%, top quartile 120%+; GRR above 90% is healthy for mid-market."
    />
  );
}

export function Headroom() {
  const [v, set] = useParams({ arr: 8_000_000, penetration: 45, capture: 15 });
  const h = headroom({ arr: v.arr, penetration: v.penetration / 100, capture: v.capture / 100 });
  return (
    <Shell
      form={<>
        <div className="eyebrow">Your book</div>
        <Money id="arr" label="Installed-base ARR" value={v.arr} onChange={set("arr")} step={100000} />
        <Pct id="penetration" label="Average catalog penetration" value={v.penetration} onChange={set("penetration")} min={5} max={100} hint="Share of your catalog's list value the average customer already owns. Postern computes this per account from the whitespace grid." />
        <Pct id="capture" label="Capture rate over 12 months" value={v.capture} onChange={set("capture")} min={0} max={60} hint="Share of unsold list value you expect to close." />
      </>}
      out={<>
        <div className="eyebrow" style={{ color: "#d9b45b" }}>Whitespace</div>
        <div className="calc__kpis">
          <Kpi k="Unsold at list" v={money0(h.unsoldAtList)} s="headroom inside accounts you already have" testid="unsold" />
          <Kpi k="Expansion ARR (12 mo)" v={money0(h.expansionArr)} s={`at ${v.capture}% capture`} testid="expansion" />
          <Kpi k="NRR contribution" v={`+${(h.nrrPoints * 100).toFixed(1)} pts`} s="on top of gross retention" />
        </div>
        <div className="gov"><div className="gov__title">What this assumes</div><div className="gov__why">Module prices roughly proportional to value; every account can in principle buy every module. In Postern the number is exact: each account's open cells are priced at list against its own seat count, and the renewal queue shows headroom next to the renewal date.</div></div>
        <ShareRow name="headroom" />
      </>}
    />
  );
}

export function RenewalUplift() {
  const [v, set] = useParams({ book: 10_000_000, renewing: 60, uplift: 5, acceptance: 80, years: 3 });
  const u = renewalUplift({ bookArr: v.book, renewingShare: v.renewing / 100, upliftPct: v.uplift, acceptance: v.acceptance / 100 });
  const f = multiYearFactor({ years: v.years, upliftPct: v.uplift });
  return (
    <Shell
      form={<>
        <div className="eyebrow">This renewal cycle</div>
        <Money id="book" label="Book ARR" value={v.book} onChange={set("book")} step={100000} />
        <Pct id="renewing" label="Share of the book renewing in the period" value={v.renewing} onChange={set("renewing")} />
        <div className="line" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <Pct id="uplift" label="Annual uplift" value={v.uplift} onChange={set("uplift")} max={20} hint="Applied on the contract anniversary, not the calendar year." />
          <Pct id="acceptance" label="Acceptance rate" value={v.acceptance} onChange={set("acceptance")} hint="Share of renewals that accept the uplift." />
        </div>
        <Num id="years" label="Multi-year term (years)" value={v.years} onChange={set("years")} min={1} max={5} />
      </>}
      out={<>
        <div className="eyebrow" style={{ color: "#d9b45b" }}>Uplift</div>
        <div className="calc__kpis">
          <Kpi k="ARR renewing" v={money0(u.renewingArr)} s={`${money0(u.acceptedArr)} accepts the uplift`} />
          <Kpi k="Incremental ARR" v={money0(u.incrementalArr)} s="from price alone, no new modules" testid="incremental" />
          <Kpi k="NRR contribution" v={`+${(u.nrrPoints * 100).toFixed(1)} pts`} />
        </div>
        <div className="gov">
          <div className="gov__title">A {v.years}-year renewal at {v.uplift}% uplift</div>
          <div className="gov__why">Total contract value is <b style={{ color: "#fff" }}>{f.toFixed(3)}×</b> first-year ARR (uplift compounds on anniversaries: {Array.from({ length: v.years }, (_, k) => Math.pow(1 + v.uplift / 100, k).toFixed(3)).join(" + ")}). Postern's one-click renewal carries the account's contracted uplift into the order form so nobody has to remember it.</div>
        </div>
        <ShareRow name="uplift" />
      </>}
    />
  );
}

export function DiscountLeakage() {
  const [v, set] = useParams({ deals: 120, acv: 40000, discount: 28, policy: 20, over: 35 });
  const l = discountLeakage({ deals: v.deals, avgNegotiated: v.acv, avgDiscount: v.discount / 100, policy: v.policy / 100, shareOver: v.over / 100 });
  return (
    <Shell
      form={<>
        <div className="eyebrow">Your deal flow</div>
        <div className="line" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <Num id="deals" label="Expansion & renewal deals per year" value={v.deals} onChange={set("deals")} max={20000} />
          <Money id="acv" label="Average negotiated ACV" value={v.acv} onChange={set("acv")} step={1000} />
        </div>
        <Pct id="discount" label="Average discount actually given" value={v.discount} onChange={set("discount")} max={70} />
        <Pct id="policy" label="Discount threshold in your policy" value={v.policy} onChange={set("policy")} max={70} hint="Above this a deal should go to deal desk." />
        <Pct id="over" label="Share of deals over the threshold" value={v.over} onChange={set("over")} hint="Honest estimate. In Postern this is a report." />
      </>}
      out={<>
        <div className="eyebrow" style={{ color: "#d9b45b" }}>Leakage</div>
        <div className="calc__kpis">
          <Kpi k="Deals that should hit deal desk" v={Math.round(l.dealsOver).toString()} s="per year" />
          <Kpi k="ARR given away beyond policy" v={money0(l.leakedArr)} s="per year, at list" testid="leaked" tone={l.leakedArr > 0 ? "#f5c3c3" : "#bfe6d1"} />
          <Kpi k="Total discount granted" v={money0(l.discountGivenTotal)} s={`avg list ${money0(l.avgList)}`} />
        </div>
        <div className="gov"><div className="gov__title">Why policy enforcement is a revenue line</div><div className="gov__why">Every point of discount beyond threshold that never reached a decision is recurring revenue, not a one-off. Postern computes the discount against the catalog — not a number the rep typed — and routes anything over threshold before the customer ever sees a price.</div></div>
        <ShareRow name="leakage" />
      </>}
    />
  );
}

export function ToolTax() {
  const [v, set] = useParams({ deals: 200, hours: 6, cost: 90, reduction: 60 });
  const t = toolTax({ deals: v.deals, hoursPerDeal: v.hours, hourlyCost: v.cost, reduction: v.reduction / 100 });
  return (
    <Shell
      form={<>
        <div className="eyebrow">Coordination cost</div>
        <Num id="deals" label="Expansion & renewal deals per year" value={v.deals} onChange={set("deals")} max={20000} />
        <Num id="hours" label="Hours per deal across sheet, Slack, email, e-sign, CRM" value={v.hours} onChange={set("hours")} max={80} step={0.5} hint="AM + deal desk + legal + finance time, all in." />
        <Money id="cost" label="Loaded hourly cost" value={v.cost} onChange={set("cost")} step={5} max={1000} />
        <Pct id="reduction" label="Time removed with one record" value={v.reduction} onChange={set("reduction")} max={90} />
      </>}
      out={<>
        <div className="eyebrow" style={{ color: "#d9b45b" }}>Cost of coordination</div>
        <div className="calc__kpis">
          <Kpi k="Hours per year" v={Math.round(t.hours).toLocaleString()} s="spent moving one deal between tools" />
          <Kpi k="Cost per year" v={money0(t.cost)} />
          <Kpi k="Recovered" v={money0(t.costSaved)} s={`${Math.round(t.hoursSaved).toLocaleString()} hours back to selling`} testid="saved" />
        </div>
        <div className="gov"><div className="gov__title">Against the price</div><div className="gov__why">Growth is $24,000 a year. At these inputs the coordination cost recovered is <b style={{ color: "#fff" }}>{(t.costSaved / 24000).toFixed(1)}×</b> the subscription — before any revenue from whitespace, uplift or leakage.</div></div>
        <ShareRow name="tooltax" />
      </>}
    />
  );
}
